import React, { useState } from 'react';
import { showToast } from '../../../../components/Toast';
import { formatCurrency, formatNumber } from '../../../../utils/formatters';
import { MESI_DISPONIBILI, OGGI, RIADDEBITI, SOCIETA, Riaddebito, etichettaMese, formatDataOra, whInKwh } from './dati';
import { Kpi } from './componenti';

type Conferma = 'chiudi' | 'riapri' | null;

function conta(n: number, singolare: string, plurale: string): string {
  return `${n} ${n === 1 ? singolare : plurale}`;
}

function esclusi(anomale: number, inCorso: number): string {
  const parti: string[] = [];
  if (anomale > 0) parti.push(conta(anomale, 'ricarica anomala', 'ricariche anomale'));
  if (inCorso > 0) parti.push(conta(inCorso, 'ricarica in corso', 'ricariche in corso'));
  return parti.join(' e ');
}

export default function VistaRiaddebiti() {
  const [societaId, setSocietaId] = useState('1');
  const [mese, setMese] = useState(MESI_DISPONIBILI[0]);
  const [riaddebiti, setRiaddebiti] = useState<Record<string, Riaddebito>>(RIADDEBITI);
  const [conferma, setConferma] = useState<Conferma>(null);

  const chiave = `${societaId}-${mese}`;
  const dati = riaddebiti[chiave] ?? null;
  const chiuso = dati?.periodo.stato === 'CHIUSO';
  // Si chiude solo un mese finito (ora italiana): il mese corrente resta aperto
  const meseFinito = mese < OGGI.slice(0, 7);
  const nomeMese = etichettaMese(mese).toLowerCase();
  const daEscludere = dati ? esclusi(dati.avvisi.anomale, dati.avvisi.inCorso) : '';

  const aggiornaPeriodo = (periodo: Riaddebito['periodo']) => {
    if (!dati) return;
    setRiaddebiti({ ...riaddebiti, [chiave]: { ...dati, periodo } });
    setConferma(null);
  };

  const esporta = () => {
    showToast(
      'Nel mockup non si scarica nulla: il file avrà un foglio di riepilogo per dipendente e uno con il dettaglio delle ricariche',
      'info',
    );
  };

  return (
    <div className="bop-ricarica__stack">
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2 className="bop-page-title">Riaddebiti</h2>
          <p className="bop-page-subtitle">Ricariche dei dipendenti della società, da passare alle paghe</p>
        </div>
        <div className="bop-flex">
          <button className="bop-btn bop-btn-secondary" disabled={!dati} onClick={esporta}>Esporta Excel</button>
          {dati && !chiuso && (
            <button className="bop-btn bop-btn-primary" disabled={!meseFinito}
              title={meseFinito ? undefined : 'Il mese si chiude quando è finito'}
              onClick={() => setConferma('chiudi')}>
              Chiudi il mese
            </button>
          )}
          {dati && chiuso && (
            <button className="bop-btn bop-btn-secondary" onClick={() => setConferma('riapri')}>Riapri il mese</button>
          )}
        </div>
      </div>

      <div className="bop-filter-bar">
        <select className="bop-select" value={societaId} onChange={(e) => setSocietaId(e.target.value)}>
          {SOCIETA.map((s) => (<option key={s.id} value={s.id}>{s.nome}</option>))}
        </select>
        <select className="bop-select" value={mese} onChange={(e) => setMese(e.target.value)}>
          {MESI_DISPONIBILI.map((m) => (<option key={m} value={m}>{etichettaMese(m)}</option>))}
        </select>
      </div>

      {!dati ? (
        <div className="bop-card">
          <div className="bop-empty-state">
            <h3 className="bop-empty-state__title">Nessuna ricarica</h3>
            <p className="bop-empty-state__text">Nessuna ricarica da riaddebitare in {nomeMese}</p>
          </div>
        </div>
      ) : (
        <>
          <div className="bop-flex">
            {chiuso ? (
              <>
                <span className="bop-badge bop-badge--neutral">Chiuso</span>
                <span className="bop-ricarica__nota">
                  il {formatDataOra(dati.periodo.chiusoAt)} da {dati.periodo.chiusoDa}: i totali non cambiano più
                </span>
              </>
            ) : (
              <>
                <span className="bop-badge bop-badge--success">Aperto</span>
                <span className="bop-ricarica__nota">
                  {meseFinito
                    ? 'i totali possono ancora cambiare finché non chiudi il mese'
                    : 'mese in corso: i totali possono ancora cambiare, si chiude quando è finito'}
                </span>
              </>
            )}
          </div>

          {daEscludere && (
            <div className="bop-alert bop-alert--warning bop-mb-0">
              Fuori dal riaddebito: {daEscludere}.
              {chiuso
                ? ' Quelle confermate più avanti vanno nel primo mese aperto, come tardive.'
                : ' Le anomale entrano dopo la conferma nella pagina Sessioni.'}
            </div>
          )}

          <div className="bop-ricarica__kpi-grid">
            <Kpi label="Dipendenti" valore={String(dati.righe.length)} />
            <Kpi label="Ricariche" valore={String(dati.totali.sessioni)} />
            <Kpi label="Energia" valore={`${formatNumber(whInKwh(dati.totali.energiaWh))} kWh`} />
            <Kpi label="Da riaddebitare" valore={`${formatCurrency(dati.totali.importo)} €`} />
          </div>

          <div className="bop-card">
            <div className="bop-table-wrapper">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Dipendente</th>
                    <th>Codice fiscale</th>
                    <th className="bop-text-right">Ricariche</th>
                    <th className="bop-text-right">kWh</th>
                    <th className="bop-text-right">Importo</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {dati.righe.map((r) => (
                    <tr key={r.dipendente.id}>
                      <td>{r.dipendente.cognome} {r.dipendente.nome}</td>
                      <td className="bop-text-mono">{r.dipendente.codiceFiscale}</td>
                      <td className="bop-text-right">{r.sessioni}</td>
                      <td className="bop-text-right">{formatNumber(whInKwh(r.energiaWh))}</td>
                      <td className="bop-text-right">{formatCurrency(r.importo)} €</td>
                      <td className="bop-text-muted">
                        {r.tardive > 0 ? `${conta(r.tardive, 'tardiva', 'tardive')} da un mese già chiuso` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bop-ricarica__totali">
                    <td>Totale</td>
                    <td />
                    <td className="bop-text-right">{dati.totali.sessioni}</td>
                    <td className="bop-text-right">{formatNumber(whInKwh(dati.totali.energiaWh))}</td>
                    <td className="bop-text-right">{formatCurrency(dati.totali.importo)} €</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}

      {conferma && dati && (
        <div className="bop-modal-overlay" onClick={() => setConferma(null)}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h2 className="bop-modal__title">
                {conferma === 'chiudi' ? `Chiudere ${nomeMese}?` : `Riaprire ${nomeMese}?`}
              </h2>
              <button className="bop-modal__close" onClick={() => setConferma(null)} aria-label="Chiudi">✕</button>
            </div>
            <div className="bop-modal__body bop-ricarica__form">
              {conferma === 'chiudi' ? (
                <>
                  <p className="bop-ricarica__nota">
                    Il mese viene congelato con {conta(dati.totali.sessioni, 'ricarica', 'ricariche')}: i totali non
                    cambiano più. Le ricariche che arrivano dopo (colonnine rimaste offline, anomale confermate)
                    finiscono nel primo mese aperto, segnate come tardive.
                  </p>
                  {daEscludere && (
                    <div className="bop-alert bop-alert--warning bop-mb-0">Restano fuori: {daEscludere}.</div>
                  )}
                </>
              ) : (
                <p className="bop-ricarica__nota">
                  Riaprire un mese è un'operazione riservata ai SUPERADMIN e resta nel registro. Se il riaddebito è già
                  passato alle paghe, i totali possono cambiare. Le ricariche tardive già spostate al mese successivo
                  restano dove sono.
                </p>
              )}
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn bop-btn-secondary" onClick={() => setConferma(null)}>Annulla</button>
              {conferma === 'chiudi' ? (
                <button className="bop-btn bop-btn-primary" onClick={() => aggiornaPeriodo({
                  stato: 'CHIUSO', chiusoAt: new Date().toISOString(), chiusoDa: 'Paolo',
                })}>
                  Chiudi il mese
                </button>
              ) : (
                <button className="bop-btn bop-btn-danger" onClick={() => aggiornaPeriodo({
                  stato: 'APERTO', chiusoAt: null, chiusoDa: null,
                })}>
                  Riapri il mese
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
